/**
 * useIdleTimeout
 *
 * The app's PRIMARY inactivity control: sign the user out after a stretch
 * with no real human input (15 min for clients, 30 for staff), warning them
 * 2 minutes before so they can stay.
 *
 * Why this exists in the client at all
 * ------------------------------------
 * The server can only measure HTTP traffic, and this app polls constantly
 * (unread counts every 15s, notification bell every 60s, thread polling every
 * 8s, and more). To the backend an open tab therefore looks permanently
 * "active", which is why the old 8-hour server-side idle window never actually
 * fired — a session stayed alive until the 7-day absolute cap regardless of
 * whether a person was in the room. Bank and brokerage practice (PCI DSS
 * 8.2.8, FFIEC authentication guidance, NIST SP 800-63B AAL2) measures whether
 * a PERSON is present, which only the client can observe. The server-side
 * windows in backend/app/core/sessions.py remain, tightened, as the backstop
 * for a cookie lifted off a machine where this timer isn't running.
 *
 * What counts as activity
 * -----------------------
 * Real input only: pointerdown, keydown, touchstart, scroll, wheel, and a
 * throttled mousemove. Deliberately NOT network traffic, timers, or React
 * renders — those are what made the server-side window meaningless.
 *
 * Cross-tab
 * ---------
 * The last-activity stamp lives in localStorage, so working in one tab keeps
 * every other tab alive. Otherwise a background tab would sign you out from
 * under the tab you're actually typing in.
 *
 * Backgrounded tabs / iOS
 * -----------------------
 * Elapsed time is always recomputed from the stored timestamp, never
 * accumulated by the interval. iOS suspends JS the moment a tab or PWA
 * backgrounds, so an interval-counted clock would simply freeze and the user
 * would come back to a still-live session hours later. Instead we re-evaluate
 * on the hidden→visible transition: resume after the deadline signs out
 * immediately, which is exactly the "closed the app, came back tomorrow" case.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** Sign out after this long with no human input. PCI DSS 8.2.8 / FFIEC norm. */
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
/**
 * Staff (advisor/admin/staff) get 30 min: advisors work through long client
 * calls with the app in the background. NIST SP 800-63B AAL2 inactivity limit.
 * Must stay under the server's 60-min idle backstop in core/sessions.py.
 * Fixed by role on purpose, never per-firm configurable.
 */
export const STAFF_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
/** Mirrors _STAFF_ROLES in backend/app/core/sessions.py. */
const STAFF_ROLES = ["advisor", "admin", "staff"];

export function idleTimeoutMsForRole(role: string | null | undefined): number {
  return STAFF_ROLES.includes(role ?? "") ? STAFF_IDLE_TIMEOUT_MS : IDLE_TIMEOUT_MS;
}
/** Show the "still there?" modal this long before the deadline. */
export const IDLE_WARNING_MS = 2 * 60 * 1000;

/** Shared across tabs. Plain localStorage: it's a timestamp, not a secret. */
const LAST_ACTIVITY_KEY = "mp_last_activity";
/** mousemove fires hundreds of times a second; one write per 5s is plenty. */
const MOUSEMOVE_THROTTLE_MS = 5000;

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
];

function readLastActivity(): number {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : Date.now();
  } catch {
    // Private mode / storage disabled: fall back to "active now". The session
    // then relies on the server-side backstop rather than logging someone out
    // of a working session because their browser blocks storage.
    return Date.now();
  }
}

function writeLastActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    /* swallow — see readLastActivity */
  }
}

/** Clear the shared stamp. Call on logout so the next login starts clean. */
export function clearIdleActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* swallow */
  }
}

export interface IdleTimeoutOptions {
  /** Master switch — pass false when nobody is signed in. */
  enabled: boolean;
  /** Fired once when the deadline passes. Should sign the user out. */
  onIdleTimeout: () => void;
  idleTimeoutMs?: number;
  warningMs?: number;
}

export interface IdleTimeoutState {
  /** True while inside the warning window and not yet timed out. */
  warning: boolean;
  /** Whole seconds left before sign-out; 0 outside the warning window. */
  secondsRemaining: number;
  /** "I'm still here" — resets the clock across every tab. */
  stayActive: () => void;
}

export function useIdleTimeout({
  enabled,
  onIdleTimeout,
  idleTimeoutMs = IDLE_TIMEOUT_MS,
  warningMs = IDLE_WARNING_MS,
}: IdleTimeoutOptions): IdleTimeoutState {
  const [warning, setWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  // Ref, not state: the tick reads it without re-subscribing every render.
  const onTimeoutRef = useRef(onIdleTimeout);
  onTimeoutRef.current = onIdleTimeout;
  // Latch so a fired timeout can't re-fire on the next tick / visibility event.
  const firedRef = useRef(false);

  const markActive = useCallback(() => {
    if (firedRef.current) return;  // already signed out; don't resurrect
    writeLastActivity(Date.now());
    setWarning(false);
    setSecondsRemaining(0);
  }, []);

  const stayActive = useCallback(() => {
    markActive();
  }, [markActive]);

  useEffect(() => {
    if (!enabled) {
      firedRef.current = false;
      setWarning(false);
      setSecondsRemaining(0);
      return;
    }

    firedRef.current = false;
    writeLastActivity(Date.now());  // the session starts now, not whenever the last one ended

    let lastMouseWrite = 0;
    const onActivity = () => markActive();
    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseWrite < MOUSEMOVE_THROTTLE_MS) return;
      lastMouseWrite = now;
      markActive();
    };

    // Evaluate against the stored stamp rather than a counter, so a suspended
    // or throttled tab still gets the right answer the moment it wakes.
    const evaluate = () => {
      if (firedRef.current) return;
      const idleFor = Date.now() - readLastActivity();
      if (idleFor >= idleTimeoutMs) {
        firedRef.current = true;
        setWarning(false);
        setSecondsRemaining(0);
        onTimeoutRef.current();
        return;
      }
      const msLeft = idleTimeoutMs - idleFor;
      if (msLeft <= warningMs) {
        setWarning(true);
        setSecondsRemaining(Math.max(0, Math.ceil(msLeft / 1000)));
      } else {
        setWarning(false);
        setSecondsRemaining(0);
      }
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true, capture: true });
    }
    window.addEventListener("mousemove", onMouseMove, { passive: true, capture: true });

    // Another tab reset the clock → drop our warning immediately.
    const onStorage = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY) evaluate();
    };
    window.addEventListener("storage", onStorage);

    // The case the mobile white-screen fix made visible: background the PWA,
    // come back later. Timers were frozen the whole time, so check on resume.
    const onVisibility = () => {
      if (document.visibilityState === "visible") evaluate();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const tick = window.setInterval(evaluate, 1000);
    evaluate();

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity, { capture: true } as any);
      }
      window.removeEventListener("mousemove", onMouseMove, { capture: true } as any);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(tick);
    };
  }, [enabled, idleTimeoutMs, warningMs, markActive]);

  return { warning, secondsRemaining, stayActive };
}
